import { useEffect, useRef, useState } from 'preact/hooks'
import type { ApiResponse } from '@/api/client'
import { websApi, type WebViewDto } from '@/api/webs'
import { webinarRoomApi, type WebinarRoomRecordingUploadDto } from '@/api/webinarRoom'
import type { PickedCloudFile } from '@/components/common/upload/cloudFile'
import { useDirectBlobUpload } from '@/components/common/upload/useDirectBlobUpload'
import { useToast } from '@/hooks/useToast'
import { getDisplayErrorMessages } from '@/utils/errorHandling'
import { retryOnStaleETag } from '@/utils/etagRetry'
import { recordingContentType, recordingFileProblem } from './roomRecording'

export interface ComputerFile {
    name: string
    size: number
    contentType: string
}

interface RoomVideoUploadOptions {
    web: WebViewDto
    /** The upload registry's key, one per card and web, so a running upload outlives the card. */
    uploadKey: string
    /** What the toasts call it: "recording", "waiting video". */
    what: string
    reload: () => Promise<void>
    onPatchWeb: (patcher: (prev: WebViewDto) => WebViewDto) => void
    /** Starts the upload with the web as last read, for its ETag. */
    beginUpload: (document: WebViewDto, file: ComputerFile) => Promise<ApiResponse<WebinarRoomRecordingUploadDto>>
    beginImport: (document: WebViewDto, file: PickedCloudFile) => Promise<ApiResponse<WebViewDto>>
    /** Drops the video on its way, by its id: an upload cancelled here, or abandoned when its tab stops. */
    abandon: (videoId: string) => Promise<void>
    /** The upload or import has begun, so the card can close its chooser. */
    onStarted: () => void
}

/**
 * How the Room tab's video cards take a file (ORB-1087, ORB-1118): from the computer straight to storage, or from
 * cloud storage through the worker, then preparing, cancelling and retrying. The cards differ only in the calls that
 * start it and drop it.
 */
export function useRoomVideoUpload({ web, uploadKey, what, reload, onPatchWeb, beginUpload, beginImport, abandon, onStarted }: RoomVideoUploadOptions) {
    const { showError, showErrorUnless422 } = useToast()
    const { upload, start, cancel, dismiss } = useDirectBlobUpload(uploadKey)
    const [busy, setBusy] = useState(false)
    const [fileError, setFileError] = useState<string | null>(null)
    // The video this browser is uploading: the web reread after the upload begins may still show the one it replaced.
    const uploadingId = useRef<string | null>(null)
    const workspaceId = web.WorkspaceId
    const webId = web.Id

    useEffect(() => {
        if (upload?.status === 'done') {
            dismiss()
            void reload()
        } else if (upload?.status === 'failed') {
            showError('Upload failed', upload.error ?? 'The upload stopped.')
            dismiss()
            void reload()
        }
    }, [upload?.status])

    const refetchWeb = async (): Promise<WebViewDto | null> => {
        const fresh = await websApi.getById(workspaceId, webId)
        if (!fresh.data) return null
        const latest = fresh.data
        onPatchWeb(() => latest)
        return latest
    }

    const uploadFromComputer = async (file: File) => {
        const contentType = recordingContentType(file.name, file.type)
        const problem = recordingFileProblem({ name: file.name, type: file.type, sizeBytes: file.size })
        if (problem || !contentType) { setFileError(problem); return }
        setFileError(null)
        setBusy(true)
        const res = await retryOnStaleETag(document => beginUpload(document, { name: file.name, size: file.size, contentType }), web, refetchWeb)
        setBusy(false)
        if (!res.data) {
            showErrorUnless422('Upload not started', res, 'The upload could not start.')
            return
        }
        const videoId = res.data.RecordingId
        uploadingId.current = videoId
        onStarted()
        start(file, res.data.UploadUrl, {
            contentType,
            onUploaded: async () => {
                const done = await webinarRoomApi.completeRecordingUpload(workspaceId, webId, videoId)
                if (done.error) throw new Error(getDisplayErrorMessages(done.error, 'The file is in, but it could not be handed over for preparing.').join(' '))
            },
            onAbandoned: () => abandon(videoId),
        })
        void reload()
    }

    const importFromCloud = async (file: PickedCloudFile | null) => {
        if (!file) return
        const problem = recordingFileProblem({ name: file.fileName, type: file.mimeType, sizeBytes: file.sizeBytes })
        if (problem) { setFileError(problem); return }
        setFileError(null)
        setBusy(true)
        const res = await retryOnStaleETag(document => beginImport(document, file), web, refetchWeb)
        setBusy(false)
        if (res.error) {
            showErrorUnless422('Import not started', res, 'The import could not start.')
            return
        }
        onStarted()
        await reload()
    }

    /** Cancels the upload this browser runs, or else drops `videoId`: one another tab or the worker is bringing in. */
    const cancelUpload = async (videoId: string | null) => {
        setBusy(true)
        const target = (upload ? uploadingId.current : null) ?? videoId
        const drop = () => (target ? abandon(target) : Promise.resolve())
        if (upload) await cancel(drop)
        else await drop()
        setBusy(false)
        await reload()
    }

    const retry = async (videoId: string) => {
        setBusy(true)
        const res = await webinarRoomApi.retryRecording(workspaceId, webId, videoId)
        setBusy(false)
        if (res.error) showErrorUnless422('Not retried', res, `The ${what} could not be prepared again.`)
        await reload()
    }

    return { upload, busy, setBusy, fileError, setFileError, refetchWeb, uploadFromComputer, importFromCloud, cancelUpload, retry }
}

/** The cloud file as an import request carries it; the ETag and which video it is for are the caller's to add. */
export function importRequestOf(file: PickedCloudFile) {
    return {
        Provider: file.provider,
        CloudStorageFileId: file.fileId,
        FileName: file.fileName,
        MimeType: file.mimeType,
        SizeBytes: file.sizeBytes ?? null,
        DirectDownloadUrl: file.provider === 'Dropbox' ? file.directDownloadUrl : null,
    }
}
